import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const navLinks = [
    { name: t.nav.home, path: '/' },
    { name: t.nav.services, path: '/services' },
    { name: t.nav.about, path: '/about' },
    { name: t.nav.approach, path: '/approach' },
    { name: t.nav.contact, path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/98 shadow-[0_2px_20px_rgba(15,35,54,0.08)]' 
            : 'bg-gradient-to-b from-[rgba(15,35,54,0.35)] via-[rgba(15,35,54,0.15)] to-transparent'
        }`}
        style={{
          backdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'blur(8px)',
          WebkitBackdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'blur(8px)',
        }}
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo - larger on mobile with proper spacing */}
            <Link 
              to="/" 
              data-testid="logo-link"
              className="flex items-center"
              style={{ flex: '1 1 auto', maxWidth: 'calc(100% - 44px)' }}
            >
              <img 
                src={isScrolled 
                  ? "/logos/bhs-logo-navy.svg"
                  : "/logos/bhs-logo-white.svg"
                }
                alt="Balearic Home Solutions - Calvià · Mallorca"
                className="h-[52px] sm:h-14 md:h-14 lg:h-14 transition-opacity duration-300"
                style={{ width: '290px', maxWidth: '100%' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.slice(1) || 'home'}`}
                  className={`text-xs font-semibold uppercase tracking-widest transition-colors ${
                    isScrolled 
                      ? `text-stone-600 hover:text-navy ${location.pathname === link.path ? 'text-navy' : ''}`
                      : `text-white hover:text-white ${location.pathname === link.path ? 'text-white' : ''}`
                  }`}
                  style={{ textShadow: isScrolled ? 'none' : '0 1px 2px rgba(0,0,0,0.15)' }}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Language Toggle */}
              <div className={`flex items-center gap-1.5 ml-4 border-l pl-6 ${isScrolled ? 'border-stone-200' : 'border-white/30'}`}>
                <button
                  onClick={() => setLanguage('EN')}
                  data-testid="lang-en"
                  className={`text-xs font-semibold tracking-wider transition-colors ${
                    isScrolled
                      ? (language === 'EN' ? 'text-navy' : 'text-stone-400 hover:text-stone-600')
                      : (language === 'EN' ? 'text-white' : 'text-white/60 hover:text-white')
                  }`}
                  style={{ textShadow: isScrolled ? 'none' : '0 1px 2px rgba(0,0,0,0.15)' }}
                >
                  EN
                </button>
                <span className={isScrolled ? 'text-stone-300' : 'text-white/40'}>|</span>
                <button
                  onClick={() => setLanguage('ES')}
                  data-testid="lang-es"
                  className={`text-xs font-semibold tracking-wider transition-colors ${
                    isScrolled
                      ? (language === 'ES' ? 'text-navy' : 'text-stone-400 hover:text-stone-600')
                      : (language === 'ES' ? 'text-white' : 'text-white/60 hover:text-white')
                  }`}
                  style={{ textShadow: isScrolled ? 'none' : '0 1px 2px rgba(0,0,0,0.15)' }}
                >
                  ES
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 ml-2 flex-shrink-0 z-50 rounded-md transition-colors ${
                isScrolled 
                  ? 'text-navy hover:bg-stone-100' 
                  : 'text-white hover:bg-white/10'
              }`}
              style={{ 
                textShadow: isScrolled ? 'none' : '0 1px 3px rgba(0,0,0,0.3)',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X size={26} strokeWidth={2} /> : <Menu size={26} strokeWidth={2} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="pt-24 px-6">
              <div className="flex flex-col gap-6">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      data-testid={`mobile-nav-link-${link.path.slice(1) || 'home'}`}
                      className={`block text-2xl font-heading ${
                        location.pathname === link.path ? 'text-navy' : 'text-stone-600'
                      }`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
                
                {/* Mobile Language Toggle */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="flex items-center gap-3 pt-6 border-t border-stone-200"
                >
                  <button
                    onClick={() => setLanguage('EN')}
                    className={`text-sm font-medium ${language === 'EN' ? 'text-navy' : 'text-stone-400'}`}
                  >
                    English
                  </button>
                  <span className="text-stone-300">|</span>
                  <button
                    onClick={() => setLanguage('ES')}
                    className={`text-sm font-medium ${language === 'ES' ? 'text-navy' : 'text-stone-400'}`}
                  >
                    Español
                  </button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer data-testid="main-footer" className="bg-navy text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" onClick={() => window.scrollTo(0, 0)} data-testid="footer-logo-link" className="inline-block mb-6">
              <img 
                src="/logos/bhs-logo-white.svg"
                alt="Balearic Home Solutions"
                className="h-10 w-auto"
                style={{ maxWidth: '240px', filter: 'brightness(1)' }}
              />
            </Link>
            <p className="text-white/70 max-w-md leading-relaxed mb-4">
              {t.footer.description}
            </p>
            <p className="text-white/50 text-sm max-w-md leading-relaxed mb-6 italic">
              {t.footer.trustedBy}
            </p>
            <div className="flex flex-col gap-3 text-sm text-white/70">
              <div className="flex items-center gap-3">
                <MapPin size={16} />
                <span>Calvià, Mallorca · Balearic Islands · Spain</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} />
                <a href="mailto:info@balearichomesolutions.com" className="footer-link">
                  info@balearichomesolutions.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} />
                <a href="tel:+34671377417" className="footer-link">
                  +34 671 377 417
                </a>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-xs font-bold tracking-widest uppercase mb-6 text-white/50">
              {t.footer.services}
            </h4>
            <div className="flex flex-col gap-3">
              <Link to="/services#property-management" className="footer-link text-sm">
                {t.servicesTitles.propertyManagement}
              </Link>
              <Link to="/services#garden-pool" className="footer-link text-sm">
                {t.servicesTitles.gardenPool}
              </Link>
              <Link to="/services#renovations" className="footer-link text-sm">
                {t.servicesTitles.renovations}
              </Link>
              <Link to="/services#maintenance" className="footer-link text-sm">
                {t.servicesTitles.maintenanceCoordination}
              </Link>
              <Link to="/services#yacht" className="footer-link text-sm">
                {t.servicesTitles.yacht}
              </Link>
            </div>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-bold tracking-widest uppercase mb-6 text-white/50">
              {t.footer.company}
            </h4>
            <div className="flex flex-col gap-3">
              <Link to="/about" className="footer-link text-sm">{t.footer.aboutUs}</Link>
              <Link to="/approach" className="footer-link text-sm">{t.footer.ourApproach}</Link>
              <Link to="/services" className="footer-link text-sm">{t.footer.allServices}</Link>
              <Link to="/contact" className="footer-link text-sm">{t.nav.contact}</Link>
            </div>
            <h4 className="text-xs font-bold tracking-widest uppercase mt-8 mb-4 text-white/50">
              {t.footer.areas}
            </h4>
            <div className="flex flex-col gap-3">
              <Link to="/property-management-mallorca" data-testid="footer-link-pm-mallorca" className="footer-link text-sm">Property Management Mallorca</Link>
              <Link to="/bendinat" data-testid="footer-link-bendinat" className="footer-link text-sm">Bendinat</Link>
              <Link to="/sol-de-mallorca" data-testid="footer-link-sol-de-mallorca" className="footer-link text-sm">Sol de Mallorca</Link>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/50">
              {t.footer.copyright.replace('{year}', new Date().getFullYear())}
            </p>
            <p className="text-xs text-white/40">
              {t.footer.tagline}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
